import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { CtaLinkParams } from '../components/cta';
import { EmobilityFilterContext } from '../context';
import { buildToolContextLink, isImageCached, notNullOrEmpty, removeNullOrUndefined } from './common';
import querystring from 'query-string';

/**
 * Returns statefull value of element visibility on screen.
 * @param ref Elemnets ref.
 * @param threshold visibility percentage.
 * @param offset offset condition. Minimum position of intersection rectangle from top.
 */
export const useOnScreen = <T extends HTMLElement>(element: T, threshold = 0.5, offset = 0) => {
	const [isIntersecting, setIntersecting] = useState(false);
	const [position, setPosition] = useState<'aboveTop' | 'bellowTop'>('bellowTop');

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				setIntersecting(entry.isIntersecting && entry.intersectionRect.top >= offset);
				setPosition(entry.boundingClientRect.y < 0 ? 'aboveTop' : 'bellowTop');
			},
			{ threshold }
		);
		if (element)
			observer.observe(element);
		return () => { observer.disconnect(); };
	}, [threshold, offset, element]);

	return [isIntersecting, position] as [boolean, 'aboveTop' | 'bellowTop'];
};

/**
 * Stores value and returns previous stored value.
 * @param value Value to observe.
 */
export const usePrev = <T extends any>(value: T) => {
	const ref = useRef<T>();

	useEffect(() => {
		ref.current = value;
	}, [value]);

	return ref.current;
};

/**
 * Stores value if defined or not zero in case of number. Returns last stored value.
 * @param value Value to observe.
 */
export const useLastDefined = <T extends any>(value: T) => {
	const [val, setVal] = useState(value);

	useEffect(() => {
		if (value)
			setVal(value);
	}, [value]);

	return val;
};

export const useTooltipOpenChange = ({ onOpen, onClose }) => {
	const [open, setOpen] = useState(false);

	const handleOnOpen = useCallback(() => {
		if (!open) {
			setOpen(true);
		}
	}, [open]);

	const handleOnClose = useCallback(() => {
		if (open) {
			setOpen(false);
		}
	}, [open]);

	useEffect(() => {
		open ? onOpen?.() : onClose?.();
	}, [open, onOpen, onClose]);

	return {
		onOpen: handleOnOpen,
		onClose: handleOnClose
	};
};

/**
 * Hook provides function which resolves value for current breakpoint.
 * @example 
 * const r = useResponsive();
 * <Grid container spacing={r({ xs: 2, md: 3 })}...
 */
export const useResponsive = () => {
	const theme = useTheme();

	const matches = {
		xs: useMediaQuery(theme.breakpoints.up('xs'), { noSsr: true }),
		sm: useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true }),
		md: useMediaQuery(theme.breakpoints.up('md'), { noSsr: true }),
		lg: useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true }),
		xl: useMediaQuery(theme.breakpoints.up('xl'), { noSsr: true })
	};

	return function <P>(responsiveValues: { [breakpoint: string]: P }) {
		const match = [...theme.breakpoints.keys]
			.reverse()
			.find(breakpoint => matches[breakpoint] && responsiveValues[breakpoint] != null);

		return match && responsiveValues[match];
	};
};

/**
 * Hook pre-loads image. Returns whether image is loaded or alredy loaded in cache.
 * @param src Image source.
 */
export const useImageLoaded = (src: string) => {
	const [imageLoaded, setImageLoaded] = useState(isImageCached(src));
	useEffect(() => {
		const image = new Image();
		if (notNullOrEmpty(src)) {
			image.src = src;
			if (image.complete)
				setImageLoaded(true);
			else
				image.onload = () => setImageLoaded(true);
		} else {
			setImageLoaded(false);
		}
		return () => image.onload = null;
	}, [src]);
	return imageLoaded;
};

/**
 * Hook tries to load image and returns result. Use to check if browser supports image format.
 * @param src image source
 * @returns undefined if processing, true if image loaded successfully, false if image load failed.
 */
export const useImageCanLoad = (src: string) => {
	const [canRender, setCanRender] = useState<boolean>();
	useEffect(() => {
		const image = new Image();
		image.src = src;
		if (image.complete)
			setCanRender(true);
		else {
			image.onload = () => setCanRender(true);
			image.onerror = () => setCanRender(false);
		}
		return () => {
			image.onload = null;
			image.onerror = null;
		};
	}, [src]);
	return canRender;
};

export const emobilityContextParametersPlaceholder = '{emobilityContextParameters}';

export const useBuildEmobilityLink = () => {
	const { current } = useContext(EmobilityFilterContext);
	const buildEmobilityLinkCallback = useCallback((template: string) => {
		const cleanParams = removeNullOrUndefined(current);
		const contextParameters = querystring.stringify(cleanParams);
		const link = template.replace(emobilityContextParametersPlaceholder, contextParameters);
		let result = Object.keys(current).reduce((prev, curr) => prev.replace(`{${curr}}`, current[curr] ?? ''), link);
		while (result.endsWith('&'))
			result = result.slice(0, -1);
		return result;
	}, [current]);
	return buildEmobilityLinkCallback;
};

export const useBuildEmobilityClgLink = () => {
	const buildEmobilityLink = useBuildEmobilityLink();
	const callback = useCallback((clgEndpoint: string, tool: string, bid: string, culture: string, dealer?: string) => {
		const template = getClgLinkTemplate(clgEndpoint, tool, dealer);
		const filledToolContext = buildToolContextLink(template, bid, culture, dealer);
		const filledEmobContext = buildEmobilityLink(filledToolContext);
		return filledEmobContext;
	}, [buildEmobilityLink]);
	return callback;
};

export const getClgLinkTemplate = (clgEndpoint: string, toolCode: string, dealer: string, params?: CtaLinkParams) => {
	const dealerParam = dealer ? '&dealerid={dealerid}' : '';
	let result = `${clgEndpoint}/api/link?tool=${toolCode}&bid={bid}&culture={culture}${dealerParam}&${emobilityContextParametersPlaceholder}`;
	if (params) {
		const validKeys = Object.keys(params).filter(key => notNullOrEmpty(params[key]?.toString()));
		validKeys.forEach(key => {
			result = result + '&' + key + `={${key}}`;
		});
	}
	return result;
};
